import { InjectProperty } from '@jack-henry/frontend-utils/di';

import '@treasury/domain-components/ip-and-time-access/ip-and-time-access.component';
import { TimeAccessType } from '@treasury/domain/backoffice/services/access/entities/time-access.entity';
import {
    CompanyUserIpRestrictions,
    CompanyUserService,
    CompanyUserTimeAccess,
    IpRestrictionAddress,
    UserTimeAccessDay,
} from '@treasury/domain/company/user';
import { TmFormChanged } from '@treasury/presentation/components/forms';
import { TmWizardStep } from '@treasury/presentation/components/tm-wizard';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { mapValidationNodes } from '@treasury/utils/validators';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

interface CompanyUserAccessModel {
    ip: {
        restricted: boolean;
        ipAddresses: IpRestrictionAddress[];
    };
    timeAccess: {
        restricted: boolean;
        days: TimeAccessDayModel[];
    };
}

interface TimeAccessDayModel extends Omit<UserTimeAccessDay, 'timeAccessType'> {
    timeAccessType: {
        label: string;
        value: TimeAccessType;
    };
}

interface FormModel {
    ipRestricted: boolean;
    timeAccessRestricted: boolean;
}
@customElement('tm-company-user-ip-and-time-access')
export class CompanyUserIpAndTimeAccess extends TmWizardStep {
    public stepLabel = 'IP and Time Access';

    @property({ type: Number })
    declare private readonly companyId: number;

    @property({ type: Number })
    declare private readonly userId: number;

    @InjectProperty()
    declare private readonly companyUserService: CompanyUserService;

    @InjectProperty()
    declare private readonly toastService: TmToastService;

    @state()
    private loading = true;

    @state()
    private ipRestrictions?: CompanyUserIpRestrictions;

    @state()
    private timeAccess?: CompanyUserTimeAccess;

    @state()
    private isValid = false;

    @state()
    private isSaving = false;

    public validate() {
        return true;
    }

    public render() {
        if (this.loading) {
            return html`<div>Loading...</div>`;
        }

        const ipRestricted =
            this.ipRestrictions?.restrictedPending ?? this.ipRestrictions?.restricted ?? false;
        const timeAccessRestricted =
            this.timeAccess?.restrictedPending ?? this.timeAccess?.restricted ?? false;

        return html` <tm-form
            @form-changed=${(e: TmFormChanged<FormModel>) => this.handleFormChange(e)}
            @form-submitted=${this.handleSaveAndContinue}
        >
            <tm-bo-ip-and-time-access
                .ipAddresses=${this.ipRestrictions?.pendingRestrictedIpAddresses ?? []}
                .ipRestricted=${ipRestricted}
                .timeAccessDays=${this.timeAccess?.userTimeAccessDaysPending ?? []}
                .timeAccessRestricted=${timeAccessRestricted}
                @save=${this.handleSaveAndContinue}
            ></tm-bo-ip-and-time-access>
            ${this.renderFooter()}
        </tm-form>`;
    }

    protected async firstUpdated() {
        try {
            this.loading = true;
            this.ipRestrictions = await this.companyUserService.getCompanyUserIpRestrictions(
                this.companyId,
                this.userId
            );

            this.timeAccess = await this.companyUserService.getCompanyUserTimeAccessRestrictions(
                this.companyId,
                this.userId
            );
        } catch {
            this.toastService.show(
                'Error loading company time access information',
                TmToastPosture.Danger,
                true
            );
        } finally {
            this.loading = false;
        }
    }

    private renderFooter() {
        return html`<div class="flex justify-end gap-[.825rem]">
            <tm-jh-button
                appearance="primary"
                type="submit"
                label="Save and Continue"
                .loading=${this.isSaving}
                class="p-0"
                .disabled=${!this.isValid}
            ></tm-jh-button>
        </div>`;
    }

    private handleFormChange(e: TmFormChanged<FormModel>) {
        const model = mapValidationNodes<CompanyUserAccessModel>(e.detail.model);

        let timeAccessIsValid = true;

        if (model.timeAccess.restricted) {
            timeAccessIsValid = this.validateTimeAccess(model.timeAccess.days);
        }

        const isValid = timeAccessIsValid && e.detail.isValid;

        this.isValid = isValid;
        this.requestUpdate();
    }

    private validateTimeAccess(timeAccess: TimeAccessDayModel[] = []) {
        let isValid = true;
        if (timeAccess.length !== 7) {
            isValid = false;
        }
        timeAccess.forEach(timeAccessDay => {
            if (timeAccessDay.timeAccessType.value === TimeAccessType.TimeRange) {
                if (
                    timeAccessDay.beginTime &&
                    timeAccessDay.endTime &&
                    timeAccessDay.beginTime >= timeAccessDay.endTime
                ) {
                    isValid = false;
                }
            }
        });
        return isValid;
    }

    private async handleSaveAndContinue(e: CustomEvent<{ model: Record<string, unknown> }>) {
        try {
            this.isSaving = true;
            this.dispatchEvent(new CustomEvent('begin-save'));
            const model = mapValidationNodes<CompanyUserAccessModel>(e.detail.model);

            if (!this.ipRestrictions) {
                throw new Error('There was an error saving the ip restrictions');
            }

            this.ipRestrictions.restricted = Boolean(model.ip.restricted);
            this.ipRestrictions.restrictedIpAddresses = model.ip.ipAddresses.map(ip => {
                const [octet1, octet2, octet3, octet4] = ip.ipAddress.split('.');
                return new IpRestrictionAddress({
                    octet1: parseInt(octet1),
                    octet2: parseInt(octet2),
                    octet3: parseInt(octet3),
                    octet4: parseInt(octet4),
                    name: ip.name,
                    id: 0,
                });
            });

            if (!this.timeAccess) {
                throw new Error('There was an error saving time access');
            }

            this.timeAccess.restricted = Boolean(model.timeAccess.restricted);

            this.timeAccess.userTimeAccessDays.forEach(day => {
                const dayModel = model.timeAccess.days.find((d, index) => index === day.dayOfWeek);
                if (dayModel) {
                    day.timeAccessType = dayModel.timeAccessType.value;
                    day.beginTime = dayModel.beginTime;
                    day.endTime = dayModel.endTime;
                }
            });

            const ipSaveSuccess = await this.companyUserService.saveCompanyUserIpRestrictions(
                this.userId,
                this.ipRestrictions.restrictedIpAddresses,
                this.ipRestrictions.restricted,
                this.companyId
            );

            const timeAccessSaveSuccess =
                await this.companyUserService.saveCompanyUserTimeAccessRestrictions(
                    this.userId,
                    this.timeAccess,
                    this.companyId
                );

            if (!ipSaveSuccess || !timeAccessSaveSuccess) {
                throw new Error('Failed to save company user ip and time access information');
            } else {
                this.dispatchEvent(new CustomEvent('continue'));
                this.toastService.show(
                    'Company user IP and time access information saved successfully',
                    TmToastPosture.Success
                );
            }
        } catch {
            this.toastService.show(
                'Error saving company user ip and time access information',
                TmToastPosture.Danger,
                true
            );
        } finally {
            this.dispatchEvent(new CustomEvent('save'));
            this.isSaving = false;
        }
    }
}
